import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from "devextreme/data/data_source";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import GridLayoutManager from "../../../../gridLayoutCustomization/GridLayoutManager";
import { GRID_PAGE_SIZES, OM_RECORD_TYPE_HISTORY, CURRENCY_FORMAT, DEFAULT_LAYOUT_NAME } from "../constants";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

/**
 * Sales orders for one customer, backed by om/Transaction/GetTransactions.
 *
 * Props:
 *   - customerId : IDMASTER rowid of the customer (required)
 *   - recordType : OMTRANSACTIONHEADER.RECORDTYPE - "R" for the Orders Open tab,
 *                  "Z" for Orders History (matches uOMD0010.pagBottomChange)
 *   - gridKey    : GUID for grid state storage
 *
 * dataFields use the DTO's own casing: om/Transaction/GetTransactions serializes
 * with DefaultContractResolver, so the JSON keys are the TransactionHeaderModel
 * property names verbatim. This also keeps the remote filter/sort selectors
 * matching the CLR members exactly.
 *
 * The desktop screen shows Status on the Open tab and Fiscal Period/Year on the
 * History tab; the same split drives the default visibility here. Hidden columns
 * remain reachable through the column chooser.
 */
const CustomerOrders = (props) => {
    const { customerId, recordType, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const isHistory = recordType === OM_RECORD_TYPE_HISTORY;

    // Named grid layouts: GridLayoutManager switches currentLayout, and the state
    // storage requests carry it so each layout keeps its own saved state.
    const [currentLayout, setCurrentLayout] = useState(DEFAULT_LAYOUT_NAME);
    const loadState = useCallback(() => GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout), [gridKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout), [gridKey, currentLayout]);

    const dataSource = useMemo(() => {
        if (!customerId) {
            return [];
        }
        const store = createStore({
            key: 'ROWID',
            loadParams: { CustomerRowId: customerId, RecordType: recordType },
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.OM.GetTransactions}`,
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        const source = new DataSource({ store: store });
        // Matches uOMM0010.DFM: ORDER BY OMTransactionHeader.KEYSEQUENCE
        source.sort([{ selector: 'KEYSEQUENCE', desc: false }]);
        return source;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, recordType]);

    // Ports uOMD0010.PAS grdBottomOrdersOpenCustomDraw (Orders Open tab only).
    // The two checks are sequential in the legacy code, not if/else, so a
    // due/overdue shipping date always wins over a Hold status when both are
    // true - reproduced here in the same order.
    const onRowPrepared = useCallback((e) => {
        if (isHistory || e.rowType !== 'data' || !e.data) {
            return;
        }
        if (e.data.StatusFlag === 'H') {
            e.rowElement.style.backgroundColor = '#fff8e1';
            e.rowElement.style.color = '#000';
        }
        if (e.data.SHIPPINGDATE && new Date(e.data.SHIPPINGDATE).setHours(0, 0, 0, 0) <= new Date().setHours(0, 0, 0, 0)) {
            e.rowElement.style.backgroundColor = '#ffcdd2';
            e.rowElement.style.color = '#000';
        }
    }, [isHistory]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            className="tabbed-detail-grid"
            dataSource={dataSource}
            keyExpr="ROWID"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            remoteOperations={true}
            onRowPrepared={onRowPrepared}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={GRID_PAGE_SIZES} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={gridKey} customSave={saveState} />

            {/* Default-visible columns - mirrors grdBottomOrdersOpen / grdBottomOrdersHistory */}
            <Column dataField="RECORD" caption="Record #" />
            <Column dataField="TRANSACTIONTYPE" caption="Type" />
            <Column dataField="TRANSACTIONNUMBER" caption="Transaction #" />
            <Column dataField="TRANSACTIONDATE" caption="Transaction" dataType="date" />
            <Column dataField="SHIPPINGDATE" caption="Shipping" dataType="date" />
            <Column dataField="SHIPTOID" caption="Ship To" />
            <Column dataField="SHIPVIA" caption="Ship Via" />
            <Column dataField="STATUS" caption="Status" visible={!isHistory} />
            <Column dataField="FISCALPERIOD" caption="Fiscal Period" dataType="number" visible={isHistory} />
            <Column dataField="FISCALYEAR" caption="Fiscal Year" dataType="number" visible={isHistory} />
            <Column dataField="EquipmentId" caption="Equipment" />
            <Column dataField="DriverId" caption="Driver" />
            <Column dataField="COMMISSIONTRANSACTIONPAYGROUP" caption="Pay Group" />
            <Column dataField="SALESPERSON1" caption="Salesperson 1" />
            <Column dataField="SALESPERSON2" caption="Salesperson 2" />
            <Column dataField="SalesPerson3" caption="Salesperson 3" />
            <Column dataField="SalesPerson4" caption="Salesperson 4" />
            <Column dataField="SalesPerson5" caption="Salesperson 5" />

            {/* Column chooser (hidden by default) */}
            <Column dataField="STAGE" caption="Stage" visible={false} />
            <Column dataField="PROFILECODE" caption="Profile" visible={false} />
            <Column dataField="CUSTOMERIDENTITYID" caption="Customer" visible={false} />
            <Column dataField="CUSTOMERNAME" caption="Customer Name" visible={false} />
            <Column dataField="SHIPTONAME" caption="Ship To Name" visible={false} />
            <Column dataField="PURCHASEORDERNUMBER" caption="PO #" visible={false} />
            <Column dataField="REFERENCE" caption="Reference" visible={false} />
            <Column dataField="INVOICEDATE" caption="Invoice Date" dataType="date" visible={false} />
            <Column dataField="TRANSACTIONDUEDATE" caption="Due Date" dataType="date" visible={false} />
            <Column dataField="COMMITDATE" caption="Commit Date" dataType="date" visible={false} />
            <Column dataField="PICKSTATUS" caption="Pick Status" visible={false} />
            <Column dataField="ORDERSIGNEDFLAG" caption="Order Signed" visible={false} />
            <Column dataField="PRICELEVEL" caption="Price Level" visible={false} />
            <Column dataField="DISCOUNTPERCENT" caption="Discount %" dataType="number" visible={false} />
            <Column dataField="PARTIALSHIPMENT" caption="Partial Shipment" visible={false} />
            <Column dataField="TAXCODE" caption="Tax Code" visible={false} />
            <Column dataField="DEPARTMENT" caption="Department" visible={false} />
            <Column dataField="JOB" caption="Job" visible={false} />
            <Column dataField="FMTRIPNUMBER" caption="FM Trip #" visible={false} />
            <Column dataField="FMPUSTOPNUMBER" caption="FM PU Stop #" visible={false} />
            <Column dataField="COMMENT" caption="Comment" visible={false} />
            <Column dataField="CREATIONUSERID" caption="Created By" visible={false} />
            <Column dataField="CREATIONDATETIME" caption="Creation Date" dataType="datetime" visible={false} />
            <Column dataField="LASTUPDATEUSERID" caption="Last Updated By" visible={false} />
            <Column dataField="LASTUPDATEDATETIME" caption="Last Updated" dataType="datetime" visible={false} />

            {/* Remaining legacy query fields (uOMM0010) - hidden, available
                through the column chooser like the AI8 grid. */}
            <Column dataField="BACKORDERFLAG" caption="Back Order Flag" visible={false} />
            <Column dataField="CANCELCOMMENT" caption="Cancel Comment" visible={false} />
            <Column dataField="COMMISSIONSETTLEMENTGROUP" caption="Commission Settlement Group" visible={false} />
            <Column dataField="COMMISSIONTYPE" caption="Commission Type" visible={false} />
            <Column dataField="COMPANYID" caption="Company ID" visible={false} />
            <Column dataField="CREDITREVIEWCOMMENT" caption="Credit Review Comment" visible={false} />
            <Column dataField="CREDITREVIEWDATE" caption="Credit Review Date" dataType="date" visible={false} />
            <Column dataField="CURRENCYBASIS" caption="Currency Basis" visible={false} />
            <Column dataField="CURRENCYCONVERSIONDATE" caption="Currency Conversion Date" dataType="date" visible={false} />
            <Column dataField="CURRENCYCONVERSIONFACTOR" caption="Currency Conversion Factor" dataType="number" visible={false} />
            <Column dataField="CANCELDATE" caption="Cancel Date" dataType="date" visible={false} />
            <Column dataField="DISCOUNTTYPE" caption="Discount Type" visible={false} />
            <Column dataField="FMTRANSFERFLAG" caption="FM Transfer" visible={false} />
            <Column dataField="MODULE" caption="Module" visible={false} />
            <Column dataField="PRINTBOL" caption="Print BOL" visible={false} />
            <Column dataField="PRINTDELIVERYNOTIFICATION" caption="Print Delivery Notification" visible={false} />
            <Column dataField="PRINTINVOICE" caption="Print Invoice" visible={false} />
            <Column dataField="PRINTORDERACKNOWLEDGEMENT" caption="Print Order Acknowledgement" visible={false} />
            <Column dataField="PRINTPACKINGSLIP" caption="Print Packing Slip" visible={false} />
            <Column dataField="PRINTPICKINGSLIP" caption="Print Picking Slip" visible={false} />
            <Column dataField="REASONCODE" caption="Reason Code" visible={false} />
            <Column dataField="SALESCOMMISIONADVANCE" caption="Sales Commission Advance" visible={false} />
            <Column dataField="SALESMETHOD" caption="Sales Method" visible={false} />
            <Column dataField="SENDEMAIL" caption="Send E-Mail" visible={false} />
            <Column dataField="SENDFAX" caption="Send Fax" visible={false} />
            <Column dataField="SHIPPERCOMMENT" caption="Shipper Comment" visible={false} />
            <Column dataField="TRANSACTIONSUBTYPE" caption="Transaction Sub Type" visible={false} />
            <Column dataField="TRUCKARRIVALDATETIME" caption="Truck Arrival Date" dataType="datetime" visible={false} />
            <Column dataField="TRUCKCOMMENT" caption="Truck Comment" visible={false} />
            <Column dataField="TRUCKDEPARTDATETIME" caption="Truck Depart Date" dataType="datetime" visible={false} />
            <Column dataField="TRUCKREFERENCE" caption="Truck Reference" visible={false} />
            <Column dataField="TotalAmount" caption="Total Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="USERDEFINEDCODE_1" caption="Code 1" visible={false} />
            <Column dataField="USERDEFINEDCODE_10" caption="Code 10" visible={false} />
            <Column dataField="USERDEFINEDCODE_2" caption="Code 2" visible={false} />
            <Column dataField="USERDEFINEDCODE_3" caption="Code 3" visible={false} />
            <Column dataField="USERDEFINEDCODE_4" caption="Code 4" visible={false} />
            <Column dataField="USERDEFINEDCODE_5" caption="Code 5" visible={false} />
            <Column dataField="USERDEFINEDCODE_6" caption="Code 6" visible={false} />
            <Column dataField="USERDEFINEDCODE_7" caption="Code 7" visible={false} />
            <Column dataField="USERDEFINEDCODE_8" caption="Code 8" visible={false} />
            <Column dataField="USERDEFINEDCODE_9" caption="Code 9" visible={false} />
            <Column dataField="USERDEFINEDCOMMENT" caption="User Defined Comment" visible={false} />
            <Column dataField="USERDEFINEDDATE_1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_10" caption="Date 10" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_6" caption="Date 6" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_7" caption="Date 7" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_8" caption="Date 8" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDDATE_9" caption="Date 9" dataType="date" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_10" caption="Quantity 10" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_6" caption="Quantity 6" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_7" caption="Quantity 7" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_8" caption="Quantity 8" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDQUANTITY_9" caption="Quantity 9" dataType="number" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_1" caption="Reference 1" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_10" caption="Reference 10" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_2" caption="Reference 2" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_3" caption="Reference 3" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_4" caption="Reference 4" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_5" caption="Reference 5" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_6" caption="Reference 6" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_7" caption="Reference 7" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_8" caption="Reference 8" visible={false} />
            <Column dataField="USERDEFINEDREFERENCE_9" caption="Reference 9" visible={false} />
            <Column dataField="ItemTotal" caption="Item Total" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="TotalTax" caption="Total Tax" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="Taxed" caption="Taxed" dataType="number" visible={false} />
            <Column dataField="NonTaxed" caption="Non Taxed" dataType="number" visible={false} />
            <Column dataField="NetWeight" caption="Net Weight" dataType="number" visible={false} />
            <Column dataField="GrossWeight" caption="Gross Weight" dataType="number" visible={false} />
            <Column dataField="QuantityOrderedSum" caption="Quantity Ordered Sum" dataType="number" visible={false} />
            <Column dataField="QuantityShippedSum" caption="Quantity Shipped Sum" dataType="number" visible={false} />
            <Column dataField="QuantityBackOrderSum" caption="Quantity Back Order Sum" dataType="number" visible={false} />
            <Column dataField="BalanceDue" caption="Balance Due" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="DiscountAmount" caption="Discount Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="DiscountBasisAmount" caption="Discount Basis Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="TaxBasisAmount" caption="Tax Basis Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="FeesAdded" caption="Fees Added" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="FeesIncluded" caption="Fees Included" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignItemTotal" caption="Foreign Item Total" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignTotalAmount" caption="Foreign Total Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignBalanceDue" caption="Foreign Balance Due" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignTaxableAmount" caption="Foreign Taxable Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignNonTaxableAmount" caption="Foreign Non Taxable Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignDiscountAmount" caption="Foreign Discount Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignDiscountBasisAmount" caption="Foreign Discount Basis Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignTaxBasisAmount" caption="Foreign Tax Basis Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignPaymentsAmount" caption="Foreign Payments Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />

            <Column dataField="PaymentsAmount" caption="Payments Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="ForeignTaxAmount" caption="Foreign Tax Amount" dataType="number" format={CURRENCY_FORMAT} visible={false} />
            <Column dataField="TaxRate" caption="Tax Rate" dataType="number" visible={false} />
            <Column dataField="RecordCount" caption="Detail Record Count" dataType="number" visible={false} />

            {/* Technical columns stay hidden and out of the chooser */}
            <Column dataField="ROWID" caption="Row Id" visible={false} showInColumnChooser={false} />
            <Column dataField="R_CUSTOMER" caption="Customer Id" visible={false} showInColumnChooser={false} />
            <Column dataField="RECORDTYPE" caption="Record Type" visible={false} showInColumnChooser={false} />
            <Column dataField="KEYSEQUENCE" caption="Key Sequence" visible={false} showInColumnChooser={false} />
            <Toolbar>
                <Item location="after">
                    <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerOrders);
